import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/** The collect_results input socket the Await's results are wired into. */
export const COLLECT_RESULTS_RESULTS_SOCKET_ID = "results";

/**
 * Input sockets for collect_results: the static `results` socket the Await's
 * all_settled results are wired into, then one array socket per configured
 * item input. The item sockets are arrays so the column lists arrive whole —
 * a single-value socket would fan Collect Results out item by item.
 */
export function generateCollectResultsInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const staticSockets: DynamicSocketDefinition[] = [
    {
      id: COLLECT_RESULTS_RESULTS_SOCKET_ID,
      name: "Results",
      type: "array<json>" as SocketType,
      required: true,
      description: "The Await's results (all settled): one outcome per item",
    },
  ];

  const itemInputs = configEntryList<{ key?: unknown; label?: unknown }>(
    config.itemInputs,
  ).filter(
    (entry): entry is { key: string; label?: unknown } =>
      typeof entry.key === "string" && entry.key.length > 0,
  );

  const { sockets } = buildDynamicSockets({
    reserved: staticSockets.map((socket) => socket.id),
    version: effectiveSocketIdVersion("collect_results", config),
    markedFallback: false,
    entries: itemInputs.map((entry) => {
      const label =
        typeof entry.label === "string" && entry.label.length > 0
          ? entry.label
          : prettify(entry.key);
      return {
        name: entry.key,
        socket: {
          name: label,
          type: "array<json>" as SocketType,
          required: false,
          description: `Item input: ${label}, one value per looped item`,
        },
      };
    }),
  });

  return [...staticSockets, ...sockets];
}
